'use strict';

import _ from 'lodash';
import {enumerate} from 'ts-transformer-enumerate';
import {keys} from 'ts-transformer-keys';

import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import PropertySet, {PropertyValueType} from '~/shared/src/mm/PropertySet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Item, {
    ItemAttributes,
    ItemOnlyAttributesSettable,
    itemOnlyAttributesSettable
} from '~/shared/src/ci/Item';
import Property, {
    PropertyAttributes,
    PropertyAttributesSettable,
    propertyAttributesSettable
} from '~/shared/src/ci/Property';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import Reference, {ReferenceAttributes} from '~/shared/src/ci/Reference';
import ReferenceProperty, {
    ReferencePropertyAttributes,
    ReferencePropertyAttributesSettable,
    referencePropertyAttributesSettable
} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty from '~/shared/src/ci/EnumReferenceProperty';
import {
    DataDescriptor,
    ObjectDataDescriptorParams,
    DataObjectProperties,
    DataStructure,
    getDataObject,
    getDataObjectByOrigId,
    getDataStructurePath,
    getReferenceSetsForDataObject,
    ObjectDataDescriptor,
    buildDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    DataObject,
    DataObjects
} from '~/shared/src/utilities/action';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';

import dbDefinitionMapper from '~/backend/src/connectors/db/dbDefinitionMapper';
import {__} from '~/shared/src/utilities/messageCatalog';


export type ItemTypeData = {
    type : 'itemType',
    object : ItemType;
};
export type PropertySetData = {
    type : 'propertySet',
    object : PropertySet;
};
export type ReferenceSetData = {
    type : 'referenceSet',
    object : ReferenceSet;
};
export type ReferencePropertySetData = {
    type : 'referencePropertySet',
    object : ReferencePropertySet;
};

export type MetaModelData = ItemTypeData[] | PropertySetData[] | ReferenceSetData[] | ReferencePropertySetData[];

export type ItemData = {
    type : 'item',
    object : Item;
};
export type PropertySetDataWithItemId = PropertySetData & {
    itemId : number;
};
export type PropertyData = {
    type : 'property',
    object : Property;
};
export type EnumPropertyData = {
    type : 'enumProperty',
    object : EnumProperty;
};
export type ReferenceData = {
    type : 'reference',
    object : Reference;
};
export type ReferencePropertyDataWithReferenceId = ReferencePropertySetData & {
    referenceId : number;
};
export type ReferencePropertyData = {
    type : 'referenceProperty',
    object : ReferenceProperty;
};
export type EnumReferencePropertyData = {
    type : 'enumReferenceProperty',
    object : EnumReferenceProperty;
};

export type ObjectData =
    ItemData[]
    | ( PropertyData | EnumPropertyData | PropertySetDataWithItemId )[]
    | ReferenceData[]
    | ( ReferencePropertyData | EnumReferencePropertyData | ReferencePropertyDataWithReferenceId )[];

export type EnumValueAttributes = {
    id : number;
    key : AttributeValue | undefined;
    value : AttributeValue | undefined;
    statusId : number;
    orderNum : number;
};
export type EnumUpdateValueAttributes = Partial<Pick<EnumValueAttributes, 'key' | 'value' | 'orderNum' | 'statusId'>>;
export const enumUpdateValueAttributes = keys<EnumUpdateValueAttributes>();
export type EnumValue = {
    [ stringKey : string ] : EnumValueAttributes;
};
export type EnumUpdateValue = {
    [ stringKey : string ] : Pick<EnumValueAttributes, 'id'> & EnumUpdateValueAttributes;
};

export type ItemsAndReferencesCollection = {
    items : Item[];
    references : Reference[];
};

export type ReferenceValue = {
    id : number;
    referenceSetId : number;
    srcItemId : number;
    destItemId : number;
    statusId : number;
    modifierUserId? : string;
    modificationTime? : NDate;
    validatorUserId? : string;
    validationTime? : NDate;
    validFromTime? : NDate;
    validToTime? : NDate;
    item? : Item;
    additionalData? : ItemsAndReferencesCollection;
};

export type ResolvedDataObject = {
    item : Item;
    reference? : Reference;
};
export type ResolvedDataObjects = {
    [ label : string ] : ResolvedDataObject | ResolvedDataObject[];
};
export type ResolvedDataObjectChain = {
    [ label : string ] : ResolvedDataObject;
};
export type MessageTemplateStringResolver = ( templateString : string, rawMode? : boolean ) => string;
type DataObjectsTree = {
    [ dataObjectId : string ] : DataObjectsTree;
};

type AllowedItemAttributes = Omit<ItemAttributes, 'properties' | 'enumProperties'>;
const allowedItemAttributes = enumerate<keyof AllowedItemAttributes>() as string[];
type AllowedPropertyAttributes = PropertyAttributes;
const allowedPropertyAttributes = enumerate<keyof AllowedPropertyAttributes>() as string[];
type AllowedEnumPropertyAttributes = {
    value : EnumValue
};
const allowedEnumPropertyAttributes = enumerate<keyof AllowedEnumPropertyAttributes>() as string[];
type AllowedReferenceAttributes = Omit<ReferenceAttributes, 'properties' | 'enumProperties'>;
const allowedReferenceAttributes = enumerate<keyof AllowedReferenceAttributes>() as string[];
type AllowedReferencePropertyAttributes = ReferencePropertyAttributes;
const allowedReferencePropertyAttributes = enumerate<keyof AllowedReferencePropertyAttributes>() as string[];
type AllowedEnumReferencePropertyAttributes = {
    value : EnumValue
};
const allowedEnumReferencePropertyAttributes = enumerate<keyof AllowedEnumReferencePropertyAttributes>() as string[];

const getItemsForType = ( dataContext : string, itemType : string ) : Item[] => {
    const retVal : Item[] = [],
          items           = global.data[ dataContext ]?.items,
          schema          = global.data[ dataContext ]?.schema;

    if ( items ) {
        const itemTypeId = metaModel[ schema ].getItemType( itemType )?.id;

        if ( itemTypeId ) {
            for ( const item of Object.values( items ) ) {
                if ( item.itemTypeId === itemTypeId ) {
                    retVal.push( item );
                }
            }
        }
    }

    return retVal;
};

/**
 * Gets all referenced and non deleted items and references from given items described by itemType (referenced items), referenceType and referenceDirection.
 *
 * @param {Item[]} items
 * @param {string} dataContext
 * @param {string | '*'} [itemType=*]
 * @param {string | '*'} [referenceType=*]
 * @param {'SrcToDest' | 'DestToSrc' | '*'} [referenceDirection=*]
 * @param {boolean} [withDeleted=false]
 *
 * @return {ItemsAndReferencesCollection}
 */
const getItemsAndReferences = ( items : Item[], dataContext : string, itemType : string = '*', referenceType : string = '*', referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' = '*', withDeleted : boolean = false ) : ItemsAndReferencesCollection => {
    const retVal : ItemsAndReferencesCollection = {
        items     : [],
        references: []
    };

    // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
    if ( referenceDirection === 'SrcToDest' ) {
        referenceDirection = 'DestToSrc';
    }
    else if ( referenceDirection === 'DestToSrc' ) {
        referenceDirection = 'SrcToDest';
    }

    for ( const item of items ) {
        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if ( reference && ( withDeleted || !reference.isDeleted() ) && ( referenceType === '*' || referenceType === reference.referenceSet.referenceType.name ) ) {

                if ( referenceDirection === 'SrcToDest' ) {
                    if ( item === reference.srcItem && reference.destItem && ( withDeleted || !reference.destItem.isDeleted() ) && ( itemType === '*' || itemType === reference.destItem.itemType.name ) ) {
                        retVal.items.push( reference.destItem );
                        retVal.references.push( reference );
                    }
                }

                else if ( referenceDirection === 'DestToSrc' ) {
                    if ( item === reference.destItem && reference.srcItem && ( withDeleted || !reference.srcItem.isDeleted() ) && ( itemType === '*' || itemType === reference.srcItem.itemType.name ) ) {
                        retVal.items.push( reference.srcItem );
                        retVal.references.push( reference );
                    }
                }

                else {
                    if ( item === reference.srcItem && reference.destItem && ( withDeleted || !reference.destItem.isDeleted() ) && ( itemType === '*' || itemType === reference.destItem.itemType.name ) ) {
                        retVal.items.push( reference.destItem );
                        retVal.references.push( reference );
                    }
                    else if ( item === reference.destItem && reference.srcItem && ( withDeleted || !reference.srcItem.isDeleted() ) && ( itemType === '*' || itemType === reference.srcItem.itemType.name ) ) {
                        retVal.items.push( reference.srcItem );
                        retVal.references.push( reference );
                    }
                }
            }
        }
    }

    return retVal;
}

/**
 * Gets all referenced and items and references recursive from given items described by itemType (referenced items), referenceType and referenceDirection.
 *
 * @param {Item[]} items
 * @param {string} dataContext
 * @param {string | '*'} [itemType=*]
 * @param {string | '*'} [referenceType=*]
 * @param {'SrcToDest' | 'DestToSrc' | '*'} [referenceDirection=*]
 * @param {boolean} [stopOnDeleted=false]
 * @param {ItemsAndReferencesCollection} [itemsAndReferences={ items: [], references: [] }]
 *
 * @return {ItemsAndReferencesCollection}
 */
const getItemsAndReferencesRecursive = ( items : Item[], dataContext : string, itemType : string = '*', referenceType : string = '*', referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' = '*', stopOnDeleted : boolean = false, itemsAndReferences : ItemsAndReferencesCollection = { items: [], references: [] } ) : ItemsAndReferencesCollection => {
    let retVal : ItemsAndReferencesCollection = itemsAndReferences;

    // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
    if ( referenceDirection === 'SrcToDest' ) {
        referenceDirection = 'DestToSrc';
    }
    else if ( referenceDirection === 'DestToSrc' ) {
        referenceDirection = 'SrcToDest';
    }

    for ( const item of items ) {

        // proceed if not stop on deleted or is not deleted only
        if ( !stopOnDeleted || !item.isDeleted() ) {

            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if ( reference && ( referenceType === '*' || referenceType === reference.referenceSet.referenceType.name ) ) {
                    if ( !retVal.references.includes( reference ) ) {
                        retVal.references.push( reference );
                    }

                    // proceed if not stop on deleted or is not deleted only
                    if ( !stopOnDeleted || !reference.isDeleted() ) {

                        if (
                            ( referenceDirection === '*' || referenceDirection === 'SrcToDest' )
                            && item === reference.srcItem
                            && reference.destItem
                            && ( itemType === '*' || itemType === reference.destItem.itemType.name )
                            && !retVal.items.includes( reference.destItem )
                        ) {
                            retVal.items.push( reference.destItem );

                            retVal = getItemsAndReferencesRecursive( [ reference.destItem ], dataContext, itemType, referenceType, referenceDirection, stopOnDeleted, retVal );
                        }

                        else if (
                            ( referenceDirection === '*' || referenceDirection === 'DestToSrc' )
                            && item === reference.destItem
                            && reference.srcItem
                            && ( itemType === '*' || itemType === reference.srcItem.itemType.name )
                            && !retVal.items.includes( reference.srcItem )
                        ) {
                            retVal.items.push( reference.srcItem );

                            retVal = getItemsAndReferencesRecursive( [ reference.srcItem ], dataContext, itemType, referenceType, referenceDirection, stopOnDeleted, retVal );
                        }
                    }
                }
            }
        }
    }

    return retVal;
}

/**
 * Gets all referenced ItemTypes and ReferenceSets from given itemTypes described by targetItemType (referenced ItemTypes), referenceType and referenceDirection.
 *
 * @param {ItemType[]} itemTypes
 * @param {string} targetItemType
 * @param {string} referenceType
 * @param {'SrcToDest' | 'DestToSrc' | '*'} referenceDirection
 *
 * @return {{ itemTypes : ItemType[], references : ReferenceSet[] }}
 */
const getItemTypesAndReferenceSets = ( itemTypes : ItemType[ ], targetItemType : string, referenceType : string, referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' ) : {
    itemTypes : ItemType[ ],
    referenceSets : ReferenceSet[ ]
} => {
    const retVal : {
        itemTypes : ItemType[],
        referenceSets : ReferenceSet[]
    } = {
        itemTypes    : [],
        referenceSets: []
    };

    for ( const itemType of itemTypes ) {
        for ( const referenceSet of itemType.referenceSets ) {
            if ( referenceType === '*' || referenceType === referenceSet.referenceType.name ) {
                if ( referenceDirection === 'DestToSrc' ) {
                    if ( referenceSet.srcItemType === itemType && referenceSet.destItemType && ( targetItemType === '' || targetItemType === referenceSet.destItemType.name ) ) {
                        retVal.itemTypes.push( referenceSet.destItemType );
                        retVal.referenceSets.push( referenceSet );
                    }
                }
                else if ( referenceDirection === 'SrcToDest' ) {
                    if ( referenceSet.destItemType === itemType && referenceSet.srcItemType && ( targetItemType === '' || targetItemType === referenceSet.srcItemType.name ) ) {
                        retVal.itemTypes.push( referenceSet.srcItemType );
                        retVal.referenceSets.push( referenceSet );
                    }
                }
                else {
                    if ( referenceSet.srcItemType === itemType && referenceSet.destItemType && ( targetItemType === '' || targetItemType === referenceSet.destItemType.name ) ) {
                        retVal.itemTypes.push( referenceSet.destItemType );
                        retVal.referenceSets.push( referenceSet );
                    }
                    else if ( referenceSet.destItemType === itemType && referenceSet.srcItemType && ( targetItemType === '' || targetItemType === referenceSet.srcItemType.name ) ) {
                        retVal.itemTypes.push( referenceSet.srcItemType );
                        retVal.referenceSets.push( referenceSet );
                    }
                }
            }
        }
    }

    return retVal;
}

const dataUtilities = {

    /**
     * Find item by given item type and key
     * @param dataContext
     * @param itemType
     * @param key
     */
    findItem( dataContext : string, itemType : number | string, key : string ) : Item | undefined {
        let retVal : Item | undefined;

        const items  = global.data[ dataContext ]?.items,
              schema = global.data[ dataContext ]?.schema;

        if ( items ) {
            const itemTypeId = metaModel[ schema ].getItemType( itemType )?.id;

            if ( itemTypeId ) {
                const foundKey = _.findKey( items, item => item.itemTypeId === itemTypeId && item.key === key );

                if ( foundKey ) {
                    retVal = items[ Number( foundKey ) ];
                }
            }
        }

        return retVal;
    },

    findItems( dataContext : string, itemType : number | string ) : Item[] | undefined {
        const itemTypeName = metaModel[ schema ].getItemType( itemType )?.name;

        let retVal : Item[] | undefined;

        if ( itemTypeName ) {
            retVal = getItemsForType( dataContext, itemTypeName );

            if ( !retVal.length ) {
                retVal = undefined;
            }
        }

        return retVal;
    },

    /**
     * Find reference by given referenceSet and items
     * @param dataContext
     * @param referenceSetId
     * @param srcItemId
     * @param destItemId
     */
    findReference( dataContext : string, referenceSetId : number, srcItemId : number, destItemId : number ) : Reference | undefined {
        let retVal : Reference | undefined;

        const items      = global.data[ dataContext ]?.items,
              references = global.data[ dataContext ]?.references;

        if ( items?.[ srcItemId ] && items?.[ destItemId ] && references ) {
            // get all references between the to given items
            const referencesToCheck = _.intersection( items[ srcItemId ].references, items[ destItemId ].references ),
                  foundReferenceId  = referencesToCheck.find( refId => {
                      const ref = references[ refId ];

                      return ref.srcItemId === srcItemId && ref.destItemId === destItemId && ref.referenceSetId === referenceSetId;
                  } );

            if ( foundReferenceId ) {
                retVal = references[ foundReferenceId ];
            }
        }

        return retVal;
    },

    /**
     * Find reference by given referenceSet and items
     * @param dataContext
     * @param referenceSetId
     * @param srcItemId
     * @param destItemId
     * @param obeyDirection
     * @param includeDeleted
     */
    findReferences( dataContext : string, referenceSetId? : number | number[], srcItemId? : number, destItemId? : number, obeyDirection : boolean = false, includeDeleted : boolean = true ) : Reference[] | undefined {
        let retVal : Reference[] | undefined;

        const referenceSetIds : number[] | undefined = _.isNumber( referenceSetId ) ? [ referenceSetId ] : referenceSetId,
              deletedStatusId                        = metaModel[ global.data[ dataContext ]?.schema ]?.getStatus( 'deleted' )?.id ?? -1;

        // at least one item id must be set
        if ( !!srcItemId || !!destItemId ) {
            const items                             = global.data[ dataContext ]?.items,
                  references                        = global.data[ dataContext ]?.references,
                  referenceIdsOfSrcItem : number[]  = srcItemId
                      ? items?.[ srcItemId ]?.references
                      ?.filter(
                          ( refId ) => {
                              return !obeyDirection || references[ refId ].srcItemId === srcItemId
                          }
                      ) ?? []
                      : [],
                  referenceIdsOfDestItem : number[] = destItemId
                      ? items?.[ destItemId ]?.references
                      ?.filter(
                          ( refId ) => {
                              return !obeyDirection || references[ refId ].destItemId === destItemId
                          }
                      ) ?? []
                      : [],
                  relevantReferenceIds              =
                      // special handling if src and dest item are identically
                      srcItemId === destItemId
                          ? referenceIdsOfSrcItem
                          : (
                              srcItemId && destItemId
                                  ? _.intersection( referenceIdsOfSrcItem, referenceIdsOfDestItem )
                                  : (
                                      referenceIdsOfSrcItem.length && referenceIdsOfDestItem.length
                                          ? _.intersection( referenceIdsOfSrcItem, referenceIdsOfDestItem )
                                          : (
                                              referenceIdsOfSrcItem.length
                                                  ? referenceIdsOfSrcItem
                                                  : referenceIdsOfDestItem
                                          )
                                  )
                          );

            if ( relevantReferenceIds.length ) {
                retVal = relevantReferenceIds.map(
                        ( refId : number ) : Reference | undefined => {
                            return !referenceSetIds || referenceSetIds.includes( references[ refId ].referenceSetId )
                                ? references[ refId ]
                                : undefined;
                        }
                    )
                    .filter(
                        ( ref ) => {
                            return !!ref && ( srcItemId !== destItemId || ref?.srcItemId === ref?.destItemId ) && ( includeDeleted || ref.statusId !== deletedStatusId )
                        }
                    ) as Reference[];
            }
        }

        return retVal;
    },

    /**
     * Find reference by given referenceSet and items
     * @param dataContext
     * @param referenceId
     */
    getItemsOfReference( dataContext : string, referenceId : number ) : Item[] | undefined {
        let retVal : Item[] | undefined;

        const items = global.data[ dataContext ]?.items;
        const references = global.data[ dataContext ]?.references;

        if ( references[ referenceId ] ) {
            retVal = [
                items[ references[ referenceId ].srcItemId ],
                items[ references[ referenceId ].destItemId ]
            ];
        }

        return retVal;
    },

    /**
     * Find reference by given referenceSet and items
     * @param dataContext
     * @param referenceId
     * @param oneItemId
     */
    getOtherItemOfReference( dataContext : string, referenceId : number, oneItemId : number ) : Item | undefined {
        let retVal : Item | undefined;

        const itemsOfReference = this.getItemsOfReference( dataContext, referenceId );
        if ( itemsOfReference[ 0 ].id === oneItemId || itemsOfReference[ 1 ].id === oneItemId ) {
            retVal = itemsOfReference[ 0 ].id === oneItemId ? itemsOfReference[ 1 ] : itemsOfReference[ 0 ];
        }

        return retVal;
    },

    /**
     * Gets the data recursive from global.data starting with rootItemId.
     *
     * @param {string} dataContext
     * @param {number} rootItemId
     * @param {boolean} [stopOnDeleted=true]
     *
     * @return {ItemsAndReferencesCollection}
     */
    getReferencedData( dataContext : string, rootItemId : number, stopOnDeleted : boolean = true ) : ItemsAndReferencesCollection {
        const rootItem = global.data[ dataContext ]?.items[ rootItemId ];

        let retVal : ItemsAndReferencesCollection = {
            items     : [],
            references: []
        };

        if ( rootItem ) {
            retVal = getItemsAndReferencesRecursive( [ rootItem ], dataContext, '*', '*', '*', stopOnDeleted, { items: [ rootItem ], references: [] } );
        }

        return retVal;
    },

    /**
     * Gets the data from global.data described by given dataDescriptor and dataStructure starting with rootItemId.
     *
     * @param {DataDescriptor} dataDescriptor
     * @param {DataStructure} dataStructure
     * @param {string} dataContext
     * @param {number} rootItemId
     * @param {boolean} withDeleted
     *
     * @return {ObjectData | undefined}
     */
    getData( dataDescriptor : ObjectDataDescriptor, dataStructure : DataStructure, dataContext : string, rootItemId? : number, withDeleted : boolean = false ) : ObjectData | undefined {
        let retVal : ObjectData | undefined;

        const objectDataObjectId = dataDescriptor.dataObject.id,
              dataStructurePath  = getDataStructurePath( objectDataObjectId, dataStructure );

        if ( dataStructurePath.length ) {
            let currentDataObject : DataObjectProperties | undefined = getDataObject( dataStructurePath.shift() ?? '', dataStructure ),
                currentItems : Item[]                                = [],
                currentReferences : Reference[]                      = [];

            if ( rootItemId ) {
                const rootItem = global.data[ dataContext ]?.items[ rootItemId ];

                if ( rootItem && ( withDeleted || !rootItem.isDeleted() ) ) {
                    currentItems = [ rootItem ];
                }
            }
            else if ( currentDataObject?.item.objectType ) {
                currentItems = getItemsForType( dataContext, currentDataObject.item.objectType );
            }

            while ( dataStructurePath.length && currentDataObject && currentItems.length ) {
                const nextDataObject = getDataObject( dataStructurePath.shift() ?? '', dataStructure );

                let nextItems : Item[]           = [],
                    nextReferences : Reference[] = [];

                if ( nextDataObject?.reference?.referenceConfiguration ) {
                    const referenceTypes = Object.keys( nextDataObject.reference.referenceConfiguration ) as string[];

                    for ( const referenceType of referenceTypes ) {
                        const referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceDirection ?? '*',
                              referenceRecursionMin : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMin ?? 1,
                              referenceRecursionMax : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMax ?? 1;

                        for ( let recursionCount = 1; recursionCount <= referenceRecursionMax; recursionCount++ ) {
                            ( { items: currentItems, references: currentReferences } = getItemsAndReferences( currentItems, dataContext, nextDataObject.item.objectType ?? '*', referenceType, referenceDirection, withDeleted ) );

                            if ( recursionCount >= referenceRecursionMin ) {
                                nextItems = nextItems.concat( currentItems );
                                nextReferences = nextReferences.concat( currentReferences );
                            }
                        }
                    }
                }

                currentDataObject = nextDataObject;
                currentItems = nextItems;
                currentReferences = nextReferences;
            }

            const originalCurrentItemsOrder = currentItems.map( i => i.id );

            let propertyTypeId : number | undefined,
                currentProperties : ( PropertyData | EnumPropertyData | PropertySetDataWithItemId )[] | ( ReferencePropertyData | EnumReferencePropertyData | ReferencePropertyDataWithReferenceId )[] | undefined;

            // order items by new (newest on top) and then by display attribute (Key or Name) asc
            currentItems.sort(
                ( i1, i2 ) => {
                    let innerRetVal = 0;

                    // new ones first (newest on top)
                    if ( i1.isNew() && !i2.isNew() ) {
                        innerRetVal = -1;
                    }
                    else if ( !i1.isNew() && i2.isNew() ) {
                        innerRetVal = 1;
                    }
                    else if ( i1.isNew() && i2.isNew() ) {
                        innerRetVal = i1.id > i2.id ? 1 : -1;
                    }
                    // otherwise order by display attribute
                    else {
                        innerRetVal = i1.displayAttributeValue.toLowerCase() > i2.displayAttributeValue.toLowerCase() ? 1 : -1;
                    }

                    return innerRetVal;
                }
            );

            // reorder references the same as items
            if ( dataDescriptor.type === 'reference' || dataDescriptor.type === 'referenceProperty' ) {
                const currentItemsOrder                      = currentItems.map( i => i.id ),
                      currentReferencesOrdered : Reference[] = [];

                for ( const itemId of currentItemsOrder ) {
                    const oldIndex = originalCurrentItemsOrder.findIndex( id => id === itemId );

                    if ( oldIndex !== -1 ) {
                        currentReferencesOrdered.push( currentReferences[ oldIndex ] )
                    }
                }

                if ( currentReferences.length === currentReferencesOrdered.length ) {
                    currentReferences = currentReferencesOrdered;
                }
            }

            switch ( dataDescriptor.type ) {
                case 'item':
                    if ( currentItems.length ) {
                        retVal = currentItems.map( item => {
                            return {
                                type  : 'item',
                                object: item
                            }
                        } );
                    }
                    break;

                case 'property':
                    propertyTypeId = metaModel[ global.data[ dataContext ]?.schema ]?.getPropertyType( dataDescriptor.propertyTypeName )?.id;

                    if ( propertyTypeId ) {
                        currentProperties = currentItems.reduce(
                            ( properties, item ) => {
                                const propertySet = metaModel[ global.data[ dataContext ]?.schema ]?.getPropertySetForItemTypeAndPropertyType( item.itemTypeId, propertyTypeId! );

                                if ( propertySet ) {
                                    const property : Property | EnumProperty | undefined = !propertySet.isEnumerated
                                        ? item.properties.get( propertyTypeId! )
                                        : item.enumProperties.get( propertyTypeId! );

                                    if ( property ) {
                                        if ( !propertySet.isEnumerated ) {
                                            properties.push( {
                                                type  : 'property',
                                                object: property as Property
                                            } );
                                        }
                                        else {
                                            properties.push( {
                                                type  : 'enumProperty',
                                                object: property as EnumProperty
                                            } );
                                        }
                                    }
                                    else {
                                        properties.push( {
                                            type  : 'propertySet',
                                            itemId: item.id,
                                            object: propertySet
                                        } );
                                    }
                                }

                                return properties;
                            },
                            [] as ( PropertyData | EnumPropertyData | PropertySetDataWithItemId )[]
                        );

                        if ( currentProperties.length ) {
                            retVal = currentProperties;
                        }
                    }
                    break;

                case 'reference':
                    if ( currentReferences.length ) {
                        retVal = currentReferences.map( reference => {
                            return {
                                type  : 'reference',
                                object: reference
                            }
                        } );
                    }
                    break;

                case 'referenceProperty':
                    propertyTypeId = metaModel[ global.data[ dataContext ]?.schema ]?.getPropertyType( dataDescriptor.propertyTypeName )?.id;

                    if ( propertyTypeId ) {
                        currentProperties = currentReferences.reduce(
                            ( properties, reference ) => {
                                const propertySet = metaModel[ global.data[ dataContext ]?.schema ]?.getReferencePropertySetForReferenceSetAndPropertyType( reference.referenceSetId, propertyTypeId! );

                                if ( propertySet ) {
                                    const property : ReferenceProperty | EnumReferenceProperty | undefined = !propertySet.isEnumerated
                                        ? reference.properties.get( propertyTypeId! )
                                        : reference.enumProperties.get( propertyTypeId! );

                                    if ( property ) {
                                        if ( !propertySet.isEnumerated ) {
                                            properties.push( {
                                                type  : 'referenceProperty',
                                                object: property as ReferenceProperty
                                            } );
                                        }
                                        else {
                                            properties.push( {
                                                type  : 'enumReferenceProperty',
                                                object: property as EnumReferenceProperty
                                            } );
                                        }
                                    }
                                    else {
                                        properties.push( {
                                            type       : 'referencePropertySet',
                                            referenceId: reference.id,
                                            object     : propertySet
                                        } );
                                    }
                                }

                                return properties;
                            },
                            [] as ( ReferencePropertyData | EnumReferencePropertyData | ReferencePropertyDataWithReferenceId )[]
                        );

                        if ( currentProperties.length ) {
                            retVal = currentProperties;
                        }
                    }
                    break;
            }
        }

        return retVal;
    },

    /**
     * Gets the item and the reference (from parent to item) from global.data described by given dataObject and dataStructure starting with rootItemId and for parentItemId.
     *
     * @param {DataObject} dataObject
     * @param {DataStructure} dataStructure
     * @param {string} dataContext
     * @param {number} [rootItemId]
     * @param {number} [parentItemId]
     * @param {boolean} [withDeleted=false]
     *
     * @return {{ items : Item[]; references? : Reference[]; } | undefined}
     */
    getItemsAndReferencesForDataObject( dataObject : DataObject, dataStructure : DataStructure, dataContext : string, rootItemId? : number, parentItemId? : number, withDeleted : boolean = false ) : {
        items : Item[];
        references? : Reference[];
    } | undefined {
        let retVal : { items : Item[]; references? : Reference[]; } | undefined;

        const objectDataObjectId = dataObject.id,
              dataStructurePath  = getDataStructurePath( objectDataObjectId, dataStructure );

        if ( dataStructurePath.length ) {
            let currentDataObject : DataObjectProperties | undefined = getDataObject( dataStructurePath.shift() ?? '', dataStructure ),
                currentItems : Item[]                                = [],
                currentReferences : Reference[]                      = [];

            if ( rootItemId ) {
                const rootItem = global.data[ dataContext ]?.items[ rootItemId ];

                if ( rootItem && ( withDeleted || !rootItem.isDeleted() ) ) {
                    currentItems = [ rootItem ];
                }
            }
            else if ( currentDataObject?.item.objectType ) {
                currentItems = getItemsForType( dataContext, currentDataObject.item.objectType );
            }

            while ( dataStructurePath.length && currentDataObject && currentItems.length ) {
                const nextDataObject = getDataObject( dataStructurePath.shift() ?? '', dataStructure );

                let nextItems : Item[]           = [],
                    nextReferences : Reference[] = [];

                if ( nextDataObject?.reference?.referenceConfiguration ) {
                    const referenceTypes = Object.keys( nextDataObject.reference.referenceConfiguration ) as string[];

                    for ( const referenceType of referenceTypes ) {
                        const referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceDirection ?? '*',
                              referenceRecursionMin : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMin ?? 1,
                              referenceRecursionMax : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMax ?? 1;

                        for ( let recursionCount = 1; recursionCount <= referenceRecursionMax; recursionCount++ ) {
                            ( { items: currentItems, references: currentReferences } = getItemsAndReferences( currentItems, dataContext, nextDataObject.item.objectType ?? '*', referenceType, referenceDirection, withDeleted ) );

                            if ( recursionCount >= referenceRecursionMin ) {
                                nextItems = nextItems.concat( currentItems );
                                nextReferences = nextReferences.concat( currentReferences );
                            }
                        }
                    }
                }
                // filter for parentItemId if
                else if ( !dataStructurePath.length && parentItemId ) {
                    for ( let i = nextReferences.length - 1; i >= 0; i-- ) {
                        if ( nextReferences[ i ].srcItemId !== parentItemId && nextReferences[ i ].destItemId !== parentItemId ) {
                            nextItems.splice( i, 1 );
                            nextReferences.splice( i, 1 );
                        }
                    }
                }

                currentDataObject = nextDataObject;
                currentItems = nextItems;
                currentReferences = nextReferences;
            }

            if ( currentItems.length ) {
                retVal = {
                    items: currentItems
                };
            }
            if ( retVal && currentReferences.length ) {
                retVal.references = currentReferences;
            }
        }

        return retVal;
    },

    /**
     * Gets the metaModel data from global.data described by given dataDescriptor and dataStructure starting with rootItemId
     * OR if no rootItemId given only from dataStructure
     *
     * @param {ObjectDataDescriptor} dataDescriptor
     * @param {DataStructure} dataStructure
     * @param {Schema} schema
     * @param {string} [dataContext]
     * @param {number} [rootItemId]
     *
     * @return {MetaModelData | undefined}
     */
    getMetaModelData( dataDescriptor : ObjectDataDescriptor, dataStructure : DataStructure, schema : Schema, dataContext? : string, rootItemId? : number ) : MetaModelData | undefined {
        let retVal : MetaModelData | undefined;

        const objectDataObjectId = dataDescriptor.dataObject.id,
              dataStructurePath  = getDataStructurePath( objectDataObjectId, dataStructure );

        if ( dataStructurePath.length ) {
            const rootItem = dataContext && rootItemId ? global.data[ dataContext ]?.items[ rootItemId ] : undefined;

            let currentDataObject : DataObjectProperties | undefined = getDataObject( dataStructurePath.shift() ?? '', dataStructure ),
                currentItemTypes : ItemType[]                        = [],
                currentReferenceSets : ReferenceSet[]                = [];

            const currentDataObjectSchema = currentDataObject?.item.objectSchema?.toUpperCase() as Schema ?? schema;

            if ( rootItem ) {
                currentItemTypes = [ rootItem.itemType ];
            }
            else if ( currentDataObject?.item.objectType && metaModel[ currentDataObjectSchema ].getItemType( currentDataObject.item.objectType ) ) {
                currentItemTypes = [ metaModel[ currentDataObjectSchema ].getItemType( currentDataObject.item.objectType )! ]
            }
            else {
                currentItemTypes = metaModel[ schema ].getItemTypes();
            }

            while ( dataStructurePath.length && currentDataObject && currentItemTypes.length ) {
                const nextDataObject                     = getDataObject( dataStructurePath.shift() ?? '', dataStructure ),
                      nextItemTypes : ItemType[]         = [],
                      nextReferenceSets : ReferenceSet[] = [];

                if ( nextDataObject?.reference?.referenceConfiguration ) {
                    const referenceTypes = Object.keys( nextDataObject.reference.referenceConfiguration ) as string[];

                    for ( const referenceType of referenceTypes ) {
                        const referenceDirection : 'SrcToDest' | 'DestToSrc' | '*' = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceDirection ?? '*',
                              referenceRecursionMin : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMin ?? 1,
                              referenceRecursionMax : number                       = nextDataObject.reference.referenceConfiguration[ referenceType ].referenceRecursionMax ?? 1;

                        for ( let recursionCount = 1; recursionCount <= referenceRecursionMax; recursionCount++ ) {
                            ( { itemTypes: currentItemTypes, referenceSets: currentReferenceSets } = getItemTypesAndReferenceSets( currentItemTypes, nextDataObject.item.objectType ?? '*', referenceType, referenceDirection ) );

                            if ( recursionCount >= referenceRecursionMin ) {
                                for ( const itemType of currentItemTypes ) {
                                    if ( !nextItemTypes.includes( itemType ) ) {
                                        nextItemTypes.push( itemType );
                                    }
                                }
                                for ( const referenceSet of currentReferenceSets ) {
                                    if ( !nextReferenceSets.includes( referenceSet ) ) {
                                        nextReferenceSets.push( referenceSet );
                                    }
                                }
                            }
                        }
                    }
                }

                currentDataObject = nextDataObject;
                currentItemTypes = nextItemTypes;
                currentReferenceSets = nextReferenceSets;
            }

            let currentPropertySets : PropertySetData[] | ReferencePropertySetData[] | undefined;

            switch ( dataDescriptor.type ) {
                case 'item':
                    if ( currentItemTypes.length ) {
                        retVal = currentItemTypes.map( item => {
                            return {
                                type  : 'itemType',
                                object: item
                            }
                        } );
                    }
                    break;

                case 'property':
                    currentPropertySets = currentItemTypes.reduce(
                        ( propertySets, itemType ) => {
                            const propertySet = metaModel[ currentDataObjectSchema ].getPropertySetForItemTypeAndPropertyType( itemType.id, dataDescriptor.propertyTypeName );

                            if ( propertySet ) {
                                propertySets.push( {
                                    type  : 'propertySet',
                                    object: propertySet
                                } );
                            }

                            return propertySets;
                        },
                        [] as PropertySetData[]
                    );

                    if ( currentPropertySets.length ) {
                        retVal = currentPropertySets;
                    }
                    break;

                case 'reference':
                    if ( currentReferenceSets.length ) {
                        retVal = currentReferenceSets.map( referenceSet => {
                            return {
                                type  : 'referenceSet',
                                object: referenceSet
                            }
                        } );
                    }
                    break;

                case 'referenceProperty':
                    currentPropertySets = currentReferenceSets.reduce(
                        ( propertySets, referenceSet ) => {
                            const propertySet = metaModel[ currentDataObjectSchema ].getReferencePropertySetForReferenceSetAndPropertyType( referenceSet.id, dataDescriptor.propertyTypeName );

                            if ( propertySet ) {
                                propertySets.push( {
                                    type  : 'referencePropertySet',
                                    object: propertySet
                                } );
                            }

                            return propertySets;
                        },
                        [] as ReferencePropertySetData[]
                    );

                    if ( currentPropertySets.length ) {
                        retVal = currentPropertySets;
                    }
                    break;
            }
        }

        return retVal;
    },

    /**
     * Gets the metaModel label described by given dataDescriptor and dataStructure
     *
     * @param {DataDescriptor} dataDescriptor
     * @param {DataStructure} dataStructure
     * @param {Schema} schema
     *
     * @return {MetaModelData | undefined}
     */
    getMetaModelLabelMsgId( dataDescriptor : DataDescriptor, dataStructure : DataStructure, schema : Schema ) : string {
        let retVal : string = '';

        if ( dataDescriptor.type === 'item' ) {
            const itemType = dataUtilities.getMetaModelData( dataDescriptor, dataStructure, schema )?.[ 0 ].object as ItemType | undefined;

            if ( itemType && dataDescriptor.attribute === 'key' ) {
                retVal = itemType.keyLabelMsgId;
            }
            else if ( itemType && dataDescriptor.attribute === 'name' ) {
                retVal = itemType.nameLabelMsgId;
            }
            else {
                retVal = `YN.base.common.attribute.${dataDescriptor.attribute}`;
            }
        }
        else if ( dataDescriptor.type === 'property' || dataDescriptor.type === 'referenceProperty' ) {
            let propertySet : PropertySet | ReferencePropertySet | undefined;

            if ( dataDescriptor.type === 'property' ) {
                propertySet = dataUtilities.getMetaModelData( dataDescriptor, dataStructure, schema )?.[ 0 ].object as PropertySet | undefined;
                retVal = propertySet?.labelMsgId ?? '';
            }
            else if ( dataDescriptor.type === 'referenceProperty' ) {
                propertySet = dataUtilities.getMetaModelData( dataDescriptor, dataStructure, schema )?.[ 0 ].object as ReferencePropertySet | undefined;
                retVal = propertySet?.labelMsgId ?? '';
            }

            if ( dataDescriptor.attribute === 'key' ) {
                if ( propertySet?.isValueSettable ) {
                    retVal += propertySet.keyLabel;
                }
            }
            else if ( dataDescriptor.attribute === 'value' ) {
                if ( propertySet?.isKeySettable ) {
                    retVal += propertySet.valueLabel;
                }
            }
            else {
                retVal += ` ${__( `YN.base.common.attribute.${dataDescriptor.attribute}` )}`;
            }
        }

        return retVal;
    },

    /**
     * Gets the ValueType for the dataDescriptor.
     *
     * @param {DataDescriptor} dataDescriptor
     * @param {Schema} schema
     * @param {DataStructure} dataStructure
     *
     * @return {FilterValueType}
     */
    getDataValueType( dataDescriptor : DataDescriptor, schema : Schema, dataStructure? : DataStructure ) : FilterValueType {
        let retVal : FilterValueType = 'STRING';

        if ( dataDescriptor.valueType ) {
            retVal = dataDescriptor.valueType;
        }
        else {
            if ( dataDescriptor.attribute === 'statusId' || dataDescriptor.attribute === 'statusName' ) {
                retVal = 'STATUS';
            }
            else if ( dataDescriptor.aggregate === 'collect' ) {
                retVal = 'TEXT';
            }
            else if ( dataDescriptor.aggregate === 'count' || dataDescriptor.aggregate === 'count(*)' ) {
                retVal = 'INTEGER';
            }
            else {
                switch ( dataDescriptor.type ) {
                    case 'item':
                        if ( dataDescriptor.attribute === 'itemTypeName' ) {
                            retVal = 'STRING';
                        }
                        else if ( dataDescriptor.attribute === 'key' ) {
                            retVal = 'STRING';

                            const itemType = metaModel[ schema ].getItemType( String( dataDescriptor.dataObject.item.objectType ?? '' ) );

                            if ( itemType?.isKeyAutogenerated ) {
                                const onlyNumbersInKeyDefinition = itemType.keyDefinition.match( /^<N+>$/ );

                                if ( onlyNumbersInKeyDefinition ) {
                                    retVal = 'INTEGER';
                                }
                            }
                        }
                        else {
                            retVal = dbDefinitionMapper.schemas[ schema ].views[ 'item' ].getFieldType( dataDescriptor.attribute ) as PropertyValueType;
                        }
                        break;

                    case 'property':
                        if ( dataDescriptor.attribute === 'propertySetName' || dataDescriptor.attribute === 'propertyTypeName' ) {
                            retVal = 'STRING';
                        }
                        else if ( dataDescriptor.attribute === 'orderNum' ) {
                            retVal = 'INTEGER';
                        }
                        else if ( dataDescriptor.attribute === 'key' || dataDescriptor.attribute === 'value' ) {
                            const propertySet = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( String( dataDescriptor.dataObject.item.objectType ?? '' ), dataDescriptor.propertyTypeName );

                            if ( propertySet ) {
                                if ( dataDescriptor.attribute === 'key' ) {
                                    retVal = propertySet.keyType;
                                }
                                else {
                                    retVal = propertySet.valueType;
                                }
                            }
                        }
                        else {
                            retVal = dbDefinitionMapper.schemas[ schema ].views[ 'property' ].getFieldType( dataDescriptor.attribute ) as PropertyValueType;
                        }
                        break;

                    case 'reference':
                        if ( dataDescriptor.attribute === 'referenceSetName' || dataDescriptor.attribute === 'referenceTypeName' ) {
                            retVal = 'STRING';
                        }
                        else if ( dataDescriptor.attribute === 'referenceTypeNameWithDirection' ) {
                            retVal = 'REFTYPEWITHDIRECTION';
                        }
                        else if ( dataDescriptor.attribute === 'referenceEnd' ) {
                            retVal = 'REFDIRECTION';
                        }
                        else {
                            retVal = dbDefinitionMapper.schemas[ schema ].views[ 'reference' ].getFieldType( dataDescriptor.attribute ) as PropertyValueType;
                        }
                        break;

                    case 'referenceProperty':
                        if ( dataDescriptor.attribute === 'referencePropertySetName' || dataDescriptor.attribute === 'propertyTypeName' ) {
                            retVal = 'STRING';
                        }
                        else if ( dataDescriptor.attribute === 'orderNum' ) {
                            retVal = 'INTEGER';
                        }
                        else if ( dataStructure && ( dataDescriptor.attribute === 'key' || dataDescriptor.attribute === 'value' ) ) {
                            const referenceSet         = getReferenceSetsForDataObject( dataDescriptor.dataObject, dataStructure, schema )[ 0 ],
                                  referencePropertySet = referenceSet ? metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( referenceSet.id, dataDescriptor.propertyTypeName ) : undefined;

                            if ( referencePropertySet ) {
                                if ( dataDescriptor.attribute === 'key' ) {
                                    retVal = referencePropertySet.keyType;
                                }
                                else {
                                    retVal = referencePropertySet.valueType;
                                }
                            }
                        }
                        else {
                            retVal = dbDefinitionMapper.schemas[ schema ].views[ 'referenceProperty' ].getFieldType( dataDescriptor.attribute ) as PropertyValueType;
                        }
                        break;
                }
            }
        }

        return retVal;
    },

    getValueFromData( dataDescriptor : DataDescriptor | ObjectDataDescriptorParams, data : ItemData | PropertyData | EnumPropertyData | ReferenceData | ReferencePropertyData | EnumReferencePropertyData ) : AttributeValue | EnumValue | ReferenceValue | undefined {
        let retVal : AttributeValue | EnumValue | ReferenceValue | undefined;

        if ( dataDescriptor.type === 'item' && data.type === 'item' && allowedItemAttributes.includes( dataDescriptor.attribute ) ) {
            retVal = data.object[ dataDescriptor.attribute as keyof AllowedItemAttributes ];
        }

        else if ( dataDescriptor.type === 'property' && data.type === 'property' && allowedPropertyAttributes.includes( dataDescriptor.attribute ) ) {
            retVal = data.object[ dataDescriptor.attribute as keyof AllowedPropertyAttributes ];
        }

        else if ( dataDescriptor.type === 'property' && data.type === 'enumProperty' && allowedEnumPropertyAttributes.includes( dataDescriptor.attribute ) ) {
            const values : EnumValue = {};

            for ( const property of data.object.properties.values() ) {
                values[ property.stringKey ] = {
                    id      : property.id,
                    key     : property.key,
                    value   : property.value,
                    statusId: property.statusId,
                    orderNum: property.orderNum
                };
            }

            if ( Object.keys( values ).length ) {
                retVal = values as EnumValue;
            }
        }

        else if ( dataDescriptor.type === 'reference' && data.type === 'reference' && allowedReferenceAttributes.includes( dataDescriptor.attribute ) ) {
            retVal = {
                id              : data.object.id,
                referenceSetId  : data.object.referenceSetId,
                srcItemId       : data.object.srcItemId,
                destItemId      : data.object.destItemId,
                statusId        : data.object.statusId,
                modifierUserId  : data.object.modifierUserId,
                modificationTime: data.object.modificationTime,
                validatorUserId : data.object.validatorUserId,
                validationTime  : data.object.validationTime,
                validFromTime   : data.object.validFromTime,
                validToTime     : data.object.validToTime
            } as ReferenceValue;
        }

        else if ( dataDescriptor.type === 'referenceProperty' && data.type === 'referenceProperty' && allowedReferencePropertyAttributes.includes( dataDescriptor.attribute ) ) {
            retVal = data.object[ dataDescriptor.attribute as keyof AllowedReferencePropertyAttributes ];
        }

        else if ( dataDescriptor.type === 'referenceProperty' && data.type === 'enumReferenceProperty' && allowedEnumReferencePropertyAttributes.includes( dataDescriptor.attribute ) ) {
            const values : EnumValue = {};

            for ( const property of data.object.properties.values() ) {
                values[ property.stringKey ] = {
                    id      : property.id,
                    key     : property.key,
                    value   : property.value,
                    statusId: property.statusId,
                    orderNum: property.orderNum
                };
            }

            if ( Object.keys( values ).length ) {
                retVal = values as EnumValue;
            }
        }

        if ( retVal === '' ) {
            retVal = undefined;
        }

        return retVal;
    },

    getOriginalValueFromData( dataDescriptor : DataDescriptor, data : ItemData | PropertyData | EnumPropertyData | ReferenceData | ReferencePropertyData | EnumReferencePropertyData ) : AttributeValue | EnumValue | ReferenceValue | undefined {
        let retVal : AttributeValue | EnumValue | ReferenceValue | undefined;

        if ( data.object.isNew() ) {
            retVal = undefined;
        }
        else {

            if ( dataDescriptor.type === 'item' && data.type === 'item' && allowedItemAttributes.includes( dataDescriptor.attribute ) ) {
                if ( itemOnlyAttributesSettable.includes( dataDescriptor.attribute ) && data.object.isAttributeModified( dataDescriptor.attribute as keyof ItemOnlyAttributesSettable ) ) {
                    retVal = data.object.attributesModified.get( dataDescriptor.attribute as keyof ItemOnlyAttributesSettable );
                }
                else {
                    retVal = data.object[ dataDescriptor.attribute as keyof AllowedItemAttributes ];
                }
            }

            else if ( dataDescriptor.type === 'property' && data.type === 'property' && allowedPropertyAttributes.includes( dataDescriptor.attribute ) ) {
                if ( propertyAttributesSettable.includes( dataDescriptor.attribute ) && data.object.isAttributeModified( dataDescriptor.attribute as keyof PropertyAttributesSettable ) ) {
                    retVal = data.object.attributesModified.get( dataDescriptor.attribute as keyof PropertyAttributesSettable );
                }
                else {
                    retVal = data.object[ dataDescriptor.attribute as keyof AllowedPropertyAttributes ];
                }
            }

            else if ( dataDescriptor.type === 'property' && data.type === 'enumProperty' && allowedPropertyAttributes.includes( dataDescriptor.attribute ) ) {
                const values : EnumValue = {};

                for ( const property of data.object.properties.values() ) {
                    values[ property.stringKey ] = {
                        id      : property.id,
                        key     : property.attributesModified.get( 'key' ) ?? property.key,
                        value   : property.attributesModified.get( 'value' ) ?? property.value,
                        statusId: property.attributesModified.get( 'statusId' ) as number ?? property.statusId,
                        orderNum: property.attributesModified.get( 'orderNum' ) as number ?? property.orderNum
                    };
                }

                if ( Object.keys( values ).length ) {
                    retVal = values as EnumValue;
                }
            }

            else if ( dataDescriptor.type === 'reference' && data.type === 'reference' && allowedReferenceAttributes.includes( dataDescriptor.attribute ) ) {
                retVal = {
                    id              : data.object.id,
                    referenceSetId  : data.object.referenceSetId,
                    srcItemId       : data.object.srcItemId,
                    destItemId      : data.object.destItemId,
                    statusId        : data.object.attributesModified.get( 'statusId' ) as number ?? data.object.statusId,
                    modifierUserId  : data.object.modifierUserId,
                    modificationTime: data.object.modificationTime,
                    validatorUserId : data.object.validatorUserId,
                    validationTime  : data.object.validationTime,
                    validFromTime   : data.object.validFromTime,
                    validToTime     : data.object.validToTime
                } as ReferenceValue;
            }

            else if ( dataDescriptor.type === 'referenceProperty' && data.type === 'referenceProperty' && allowedReferencePropertyAttributes.includes( dataDescriptor.attribute ) ) {
                if ( referencePropertyAttributesSettable.includes( dataDescriptor.attribute ) && data.object.isAttributeModified( dataDescriptor.attribute as keyof ReferencePropertyAttributesSettable ) ) {
                    retVal = data.object.attributesModified.get( dataDescriptor.attribute as keyof ReferencePropertyAttributesSettable );
                }
                else {
                    retVal = data.object[ dataDescriptor.attribute as keyof AllowedReferencePropertyAttributes ];
                }
            }

            else if ( dataDescriptor.type === 'referenceProperty' && data.type === 'enumReferenceProperty' && allowedPropertyAttributes.includes( dataDescriptor.attribute ) ) {
                const values : EnumValue = {};

                for ( const property of data.object.properties.values() ) {
                    values[ property.stringKey ] = {
                        id      : property.id,
                        key     : property.attributesModified.get( 'key' ) ?? property.key,
                        value   : property.attributesModified.get( 'value' ) ?? property.value,
                        statusId: property.attributesModified.get( 'statusId' ) as number ?? property.statusId,
                        orderNum: property.attributesModified.get( 'orderNum' ) as number ?? property.orderNum
                    };
                }

                if ( Object.keys( values ).length ) {
                    retVal = values as EnumValue;
                }
            }
        }

        if ( retVal === '' ) {
            retVal = undefined;
        }

        return retVal;
    },

    getResolvedDataObjects( dataObjects : DataObjects, dataStructure : DataStructure, dataContext : string, rootItemId : number, includeRootObject : boolean = true ) : ResolvedDataObjects {
        const retVal : ResolvedDataObjects = {};

        try {
            if ( includeRootObject ) {
                retVal.rootObject = {
                    item: global.data[ dataContext ].items[ rootItemId ]
                };
            }

            for ( const dataObjectLabel of Object.keys( dataObjects ) ) {
                let items : Item[]           = [],
                    references : Reference[] = [];

                const dataObject          = getDataObjectByOrigId( dataObjects[ dataObjectLabel ].origId, dataStructure ),
                      itemObjectData      = dataUtilities.getData(
                          buildDataDescriptor( { attributePath: 'item.id' }, dataObject! ) as ObjectDataDescriptor,
                          dataStructure,
                          dataContext,
                          rootItemId
                      ),
                      referenceObjectData = dataUtilities.getData(
                          buildDataDescriptor( { attributePath: 'reference.id' }, dataObject! ) as ObjectDataDescriptor,
                          dataStructure,
                          dataContext,
                          rootItemId
                      );

                items = ( itemObjectData?.map( iOD => iOD.object ) ?? [] ) as Item[];
                references = ( referenceObjectData?.map( rOD => rOD.object ) ?? [] ) as Reference[];

                if ( items.length ) {
                    for ( const item of items ) {
                        const reference = references.find( ref => item.references.includes( ref.id ) );

                        const resolvedDataObject : ResolvedDataObject = {
                            item
                        };

                        if ( reference ) {
                            resolvedDataObject.reference = reference;
                        }

                        if ( dataObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                            retVal[ dataObjectLabel ] = resolvedDataObject;

                            break;
                        }
                        else {
                            if ( !retVal[ dataObjectLabel ] ) {
                                retVal[ dataObjectLabel ] = [];
                            }

                            ( retVal[ dataObjectLabel ] as ResolvedDataObject[] ).push( resolvedDataObject );
                        }
                    }
                }
                else if ( dataObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                    retVal[ dataObjectLabel ] = {} as ResolvedDataObject;
                }
                else if ( dataObjects[ dataObjectLabel ].dataSelectionMode === 'all' ) {
                    retVal[ dataObjectLabel ] = [];
                }
            }
        }
        catch ( e ) {
            console.error( e );
        }

        return retVal;
    },

    getResolvedDataObjectChains( dataObjects : DataObjects, dataStructure : DataStructure, dataContext : string, rootItemId : number, includeRootObject : boolean = true ) : ResolvedDataObjectChain[] {
        const dataObjectIdMapping : {
                  [ dataObjectId : string ] : {
                      label : string;
                      dataObject : DataObject;
                  }
              }                                 = {},
              dataObjectsTree : DataObjectsTree = {};

        for ( const label of Object.keys( dataObjects ) as string[] ) {
            const dataObject     = dataObjects[ label ],
                  dataObjectPath = getDataStructurePath( dataObject.id, dataStructure );

            dataObjectIdMapping[ dataObject.id ] = {
                label,
                dataObject
            };

            let currentTree = dataObjectsTree;

            for ( const node of dataObjectPath ) {
                if ( !currentTree[ node ] ) {
                    currentTree[ node ] = {};
                }
                currentTree = currentTree[ node ];
            }
        }

        const recursiveBuildChain = ( tree : DataObjectsTree ) : ResolvedDataObjectChain[] => {
            let chain : ResolvedDataObjectChain[] = [ {} ];

            for ( const node of Object.keys( tree ) ) {
                const dataObjectChain = recursiveBuildChain( tree[ node ] );

                let nodeChain : ResolvedDataObjectChain[] = [];

                // multiply chain with resolvedDataObjects
                if ( dataObjectIdMapping[ node ] ) {
                    const dataObject          = dataObjectIdMapping[ node ].dataObject,
                          dataObjectLabel     = dataObjectIdMapping[ node ].label,
                          resolvedDataObject  = dataUtilities.getResolvedDataObjects( { [ dataObjectLabel ]: dataObject }, dataStructure, dataContext, rootItemId, includeRootObject )[ dataObjectLabel ],
                          resolvedDataObjects = Array.isArray( resolvedDataObject ) ? resolvedDataObject : [ resolvedDataObject ];

                    for ( let i = 0; i < chain.length; i++ ) {
                        for ( let k = 0; k < resolvedDataObjects.length; k++ ) {
                            nodeChain.push( {
                                ...chain[ i ],
                                [ dataObjectLabel ]: resolvedDataObjects[ k ]
                            } );
                        }
                    }

                    if ( nodeChain.length ) {
                        chain = nodeChain;
                    }
                }

                // multiply chain with dataObjectChains
                nodeChain = [];
                for ( let i = 0; i < chain.length; i++ ) {
                    for ( let k = 0; k < dataObjectChain.length; k++ ) {
                        nodeChain.push( {
                            ...chain[ i ],
                            ...dataObjectChain[ k ]
                        } );
                    }
                }

                if ( nodeChain.length ) {
                    chain = nodeChain;
                }
            }

            return chain;
        };

        return recursiveBuildChain( dataObjectsTree );
    }
};

export default dataUtilities;
